import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, Sparkles, User as UserIcon, Lightbulb, PhoneOff, MessageCircleQuestion } from "lucide-react";

const CHIP = { good: "bg-[#2B6E4F]/10 text-[#2B6E4F]", ok: "bg-[#a05a00]/10 text-[#a05a00]", miss: "bg-[#8B2E2E]/10 text-[#8B2E2E]" };

function Setup({ cfg, onStart, starting }) {
  const [sel, setSel] = useState(() => ({
    avatar: cfg.avatars[0]?.key,
    personality: cfg.personalities[0]?.key,
    blocker: cfg.blockers[0]?.key,
    difficulty: cfg.difficulties[1]?.key || cfg.difficulties[0]?.key,
  }));
  const brief = cfg.briefs?.[sel.avatar]?.[sel.personality]?.[sel.blocker] || "";
  const set = (k) => (e) => setSel((s) => ({ ...s, [k]: e.target.value }));
  const selects = [
    ["avatar", "Prospect type", cfg.avatars], ["personality", "Personality style", cfg.personalities],
    ["blocker", "Primary blocker", cfg.blockers], ["difficulty", "Engagement level", cfg.difficulties],
  ];
  return (
    <div className="max-w-3xl">
      <div className="grid sm:grid-cols-2 gap-4">
        {selects.map(([key, label, opts]) => (
          <label key={key} className="block">
            <span className="label-eyebrow">{label}</span>
            <select value={sel[key]} onChange={set(key)} data-testid={`sim-select-${key}`}
                    className="mt-2 w-full border hairline bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[color:var(--brand-primary)]">
              {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </label>
        ))}
      </div>
      {brief && (
        <div className="mt-6 border hairline p-5 bg-[color:var(--brand-surface2)]" data-testid="sim-brief">
          <div className="label-eyebrow mb-2">Your prospect</div>
          <p className="text-sm text-neutral-700 leading-relaxed">{brief}</p>
        </div>
      )}
      <button onClick={() => onStart(sel)} disabled={starting} className="btn-primary mt-6" data-testid="sim-start-btn">
        {starting ? "Dialling…" : "Start the call"}
      </button>
    </div>
  );
}

function StageTracker({ stages, current, onSelect }) {
  return (
    <div className="border hairline bg-white divide-y divide-neutral-100" data-testid="sim-stage-tracker">
      {stages.map((s) => (
        <button key={s.num} onClick={() => onSelect(s.num)} data-testid={`sim-stage-${s.num}`}
                className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-opacity ${
                  s.num === current ? "opacity-100" : s.num < current ? "opacity-50" : "opacity-40 hover:opacity-70"}`}>
          <span className={`w-6 h-6 flex items-center justify-center text-[11px] font-mono border ${
            s.num === current ? "bg-[color:var(--brand-primary)] text-white border-transparent"
              : s.num < current ? "bg-[#2B6E4F]/10 text-[#2B6E4F] border-transparent" : "border-neutral-300 text-neutral-400"}`}>
            {s.num < current ? "✓" : s.num}
          </span>
          <span className="text-xs font-medium">{s.name}</span>
        </button>
      ))}
    </div>
  );
}

function Debrief({ data, criteria, onRestart }) {
  return (
    <div className="border-2 border-[color:var(--brand-primary)] bg-white p-6 space-y-5" data-testid="sim-debrief-panel">
      <div className="flex items-baseline justify-between">
        <div className="label-eyebrow text-[var(--brand-primary)]">Call debrief</div>
        {Number.isFinite(data.overall_score) && <div className="font-display text-4xl" data-testid="sim-debrief-score">{data.overall_score}<span className="text-lg text-neutral-400">/100</span></div>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {criteria.map((c) => {
          const v = data.scores?.[c.key];
          return (
            <div key={c.key} className="flex items-center justify-between border hairline px-3 py-2">
              <span className="text-xs">{c.label}</span>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${CHIP[v] || "bg-neutral-100 text-neutral-400"}`}>{v || "—"}</span>
            </div>
          );
        })}
      </div>
      {data.strengths && <div><div className="label-eyebrow">Strengths</div><p className="text-sm mt-1">{data.strengths}</p></div>}
      {data.development && <div><div className="label-eyebrow">Development focus</div><p className="text-sm mt-1">{data.development}</p></div>}
      {data.overall && <p className="text-sm italic border-t hairline pt-4">{data.overall}</p>}
      <button onClick={onRestart} className="btn-primary text-sm" data-testid="sim-new-call-btn">Run another call</button>
    </div>
  );
}

export default function PracticeSimulator() {
  const [cfg, setCfg] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);
  const [stage, setStage] = useState(1);
  const [showHint, setShowHint] = useState(false);
  const [debrief, setDebrief] = useState(null);
  const [debriefing, setDebriefing] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { api.get("/simulator/config").then((r) => setCfg(r.data)).catch(() => toast.error("Could not load simulator")); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const start = async (sel) => {
    setStarting(true);
    try {
      const { data } = await api.post("/simulator/start", sel);
      setSession(data);
      setMessages([]); setDebrief(null); setStage(1);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
    finally { setStarting(false); }
  };

  const send = async () => {
    if (!input.trim() || busy) return;
    const msg = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/simulator/chat", { session_id: session.id, message: msg, stage });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch { toast.error("Chat error — try again"); }
    finally { setBusy(false); }
  };

  const getFeedback = async () => {
    if (messages.length < 2 || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/simulator/feedback", { session_id: session.id });
      setMessages((m) => [...m, { role: "coach", content: data.feedback }]);
    } catch { toast.error("Feedback unavailable"); }
    finally { setBusy(false); }
  };

  const endAndDebrief = async () => {
    if (messages.length < 2) { toast("Have at least one exchange first"); return; }
    setDebriefing(true);
    try {
      const { data } = await api.post("/simulator/debrief", { session_id: session.id });
      setDebrief(data);
    } catch { toast.error("Debrief failed — try again"); }
    finally { setDebriefing(false); }
  };

  if (!cfg) return <div className="text-sm text-neutral-500">Loading simulator…</div>;

  if (!session) {
    return (
      <div className="space-y-10" data-testid="simulator-setup">
        <div>
          <Link to="/scenarios" className="label-eyebrow" data-testid="sim-back">← Role-Play Studio</Link>
          <div className="label-eyebrow mb-3 mt-4 text-[var(--brand-primary)]">Practice Simulator</div>
          <h1 className="font-display text-5xl md:text-6xl leading-[1.02]">Build your prospect. <em className="italic">Run the call.</em></h1>
          <p className="mt-4 text-neutral-500 max-w-2xl">Choose a prospect type, personality style, blocker and engagement level — then run the staged call against a live AI prospect. Get hints, mid-call coaching, and a full debrief.</p>
        </div>
        <Setup cfg={cfg} onStart={start} starting={starting} />
      </div>
    );
  }

  const currentHint = cfg.stages[stage - 1]?.hint;

  return (
    <div className="grid lg:grid-cols-12 gap-8 min-h-[calc(100vh-14rem)]" data-testid="simulator-call">
      <aside className="lg:col-span-4 space-y-5">
        <button onClick={() => setSession(null)} className="label-eyebrow" data-testid="sim-exit">← New scenario</button>
        <div>
          <div className="label-eyebrow text-[var(--brand-primary)]">Live practice call</div>
          <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{session.brief}</p>
        </div>
        <StageTracker stages={cfg.stages} current={stage} onSelect={setStage} />
        <button onClick={() => setShowHint(!showHint)} className="btn-ghost w-full flex items-center justify-center gap-2 text-sm" data-testid="sim-hint-btn">
          <Lightbulb size={14} /> {showHint ? "Hide hint" : `Hint for Stage ${stage}`}
        </button>
        {showHint && <div className="border hairline p-4 bg-[color:var(--brand-surface2)] text-sm leading-relaxed" data-testid="sim-hint-panel">{currentHint}</div>}
        <div className="flex gap-2">
          <button onClick={getFeedback} disabled={busy || messages.length < 2} className="btn-ghost flex-1 flex items-center justify-center gap-2 text-sm" data-testid="sim-feedback-btn">
            <MessageCircleQuestion size={14} /> Get feedback
          </button>
          <button onClick={endAndDebrief} disabled={debriefing || messages.length < 2} className="btn-primary flex-1 flex items-center justify-center gap-2 text-sm" data-testid="sim-debrief-btn">
            <PhoneOff size={14} /> {debriefing ? "Scoring…" : "End & debrief"}
          </button>
        </div>
        {debrief && <Debrief data={debrief} criteria={cfg.criteria} onRestart={() => setSession(null)} />}
      </aside>

      <section className="lg:col-span-8 flex flex-col border hairline bg-white min-h-[70vh]">
        <div className="flex-1 overflow-y-auto p-6 md:p-10 space-y-6" data-testid="sim-messages">
          {messages.length === 0 && (
            <div className="text-neutral-400 text-sm">The prospect just answered the phone. Open the call&hellip;</div>
          )}
          {messages.map((m, i) => m.role === "coach" ? (
            <div key={i} className="border-l-2 border-[#a05a00] bg-[#a05a00]/5 px-4 py-3 text-sm italic" data-testid="sim-coach-msg">
              <span className="label-eyebrow text-[#a05a00] not-italic block mb-1">Coach</span>{m.content}
            </div>
          ) : (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role !== "user" && (
                <div className="w-8 h-8 border hairline flex items-center justify-center flex-shrink-0">
                  <Sparkles size={14} className="text-[var(--brand-primary)]" />
                </div>
              )}
              <div className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed ${
                m.role === "user" ? "bg-[color:var(--brand-primary)] text-white" : "bg-[color:var(--brand-surface2)] text-[var(--brand-primary)]"}`}>
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
              {m.role === "user" && (
                <div className="w-8 h-8 border hairline flex items-center justify-center flex-shrink-0"><UserIcon size={14} /></div>
              )}
            </div>
          ))}
          {busy && <div className="text-xs text-neutral-400 pl-11">Prospect is on the line…</div>}
          <div ref={bottomRef} />
        </div>
        <div className="border-t hairline p-4 flex items-end gap-2">
          <textarea rows={2} value={input} onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Your response…" data-testid="sim-input"
                    className="flex-1 bg-transparent border hairline px-3 py-2 resize-none focus:outline-none focus:border-[color:var(--brand-primary)]" />
          <button onClick={send} disabled={busy || !input.trim()} className="btn-primary flex items-center gap-2" data-testid="sim-send">
            <Send size={14} /> Send
          </button>
        </div>
      </section>
    </div>
  );
}
